// Shows Email Subscriptions for the current user
//
// Properties:
//    item: Item subs belong to

import { useState} from 'react';
import { useStatusMessage } from '../../components';
import Button from 'react-bootstrap/Button';
import * as Database from '../../utilities/Database.mjs';
import { useAutosave } from '../../utilities/useAutosave';
import { saveQueue } from '../../utilities/SaveQueue.mjs';
import {
    calculateNextSendDate,
    emailTypeItemStatus,
    emailSendDaily,
    emailSendWeekly,
    emailSendMonthly,
    emailSendQuarterly,
    emailSendEverySixMonths,
    emailSendYearly,
    digestModeSummary,
    digestModeTransitions,
    digestModeBoth,
    effectiveDigestMode,
    digestModeWantsSummary,
    digestModeWantsTransitions,
} from '../../fleet-shared/Email.mjs';


const DeleteButton = ({ deletingId, subId, onDelete }) => (
  <div className="d-flex align-items-center gap-2">
      <i className='p-2 fa fa-trash' aria-hidden='true' onClick={() => onDelete(subId)}></i>
      {(deletingId === subId) && (<span className='p-2'><b>Deleting...</b></span>)}
  </div>
);


const frequencies = [
    { name: 'Daily', numDays: emailSendDaily },
    { name: 'Weekly', numDays: emailSendWeekly },
    { name: 'Monthly', numDays: emailSendMonthly },
    { name: 'Quarterly', numDays: emailSendQuarterly },
    { name: 'Every 6 months', numDays: emailSendEverySixMonths },
    { name: 'Yearly', numDays: emailSendYearly },
];

// A real calendar day (today, in UTC) so DST and tz-abbreviation resolve
// correctly. Both the label and the ordering below must use the *same*
// reference day — otherwise, in a DST-observing zone, the offset that orders
// the hours (e.g. EST) can differ from the offset that labels them (e.g. EDT),
// rotating where "midnight local" lands and shifting the dropdown by an hour.
function gmtHourAsLocalDate(gmtHour) {
    const now = new Date();
    return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), gmtHour));
}

// The send-hour field is stored as an integer GMT hour (`sendHourGMT`). The
// daemon and `calculateNextSendDate` rely on that being GMT, so we only change
// the *display*: each <option> keeps the GMT hour as its value, but its label
// shows that hour in the user's local time (e.g. "6:00 PM EDT").
export function localHourLabel(gmtHour) {
    return gmtHourAsLocalDate(gmtHour)
        .toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', timeZoneName: 'short' });
}

// GMT hours 0-23, reordered so the dropdown reads top-to-bottom in the user's
// own day (midnight -> 11pm local) instead of wrapping mid-evening. The values
// stay GMT hours; only the ordering follows local time. Exported for testing.
export function localOrderGmtHours() {
    return Array.from({ length: 24 }, (_, h) => h)
        .sort((a, b) => gmtHourAsLocalDate(a).getHours() - gmtHourAsLocalDate(b).getHours());
}

// Three mutually-exclusive ways a user can be notified. The labels show up
// in the radio group; helpText becomes the small caption under the chosen
// option so the consequence of each pick is visible without a tooltip.
const digestModeOptions = [
    {
        value: digestModeSummary,
        label: 'Summary on a schedule',
        helpText: 'Full item digest (tasks, counters, recent logbook) on the schedule you pick. No extra emails between sends.',
    },
    {
        value: digestModeTransitions,
        label: 'Alerts when tasks change',
        helpText: 'Checked once a day at your send hour; emails you only when a task becomes due soon or overdue. No periodic summary.',
    },
    {
        value: digestModeBoth,
        label: 'Both',
        helpText: 'Scheduled summary plus same-day alerts when a task becomes due soon or overdue.',
    },
];

// Keeps the derived database fields (notifyOnTaskStateChange, nextSendDate,
// transitionNextSendDate) consistent with the user's edits, because the
// daemon's listing filter and gating both read those scalars directly. Runs
// on every autosave form edit (useAutosave's deriveState contract):
//
//  * a digest-mode change re-derives all three;
//  * a frequency change re-arms nextSendDate -- but only in a summary mode
//    ("transitions" intentionally has no schedule; the value is kept on the
//    row and recomputed when the user re-enters a summary mode).
function deriveSubscriptionState(previous, next) {
  const derived = { ...next };

  if (!previous || derived.digestMode !== previous.digestMode) {
    const wantsSummaryNext = digestModeWantsSummary(derived.digestMode);
    const wantsTransitionsNext = digestModeWantsTransitions(derived.digestMode);

    derived.notifyOnTaskStateChange = wantsTransitionsNext;

    if (wantsSummaryNext) {
      // Re-arm the schedule so the daemon picks the right next slot. The
      // current sendEveryDays / sendHourGMT on `derived` already reflect
      // any edits made in the same debounce window.
      derived.nextSendDate = calculateNextSendDate(derived);
    } else {
      // Transitions-only: explicit null tells AppSync to unset the field
      // (Database.updateEmailSubscription has a matching escape hatch).
      derived.nextSendDate = null;
    }

    if (wantsTransitionsNext && !derived.transitionNextSendDate) {
      // Lazy-init: schedule the next hourly daemon run to pick up this
      // subscription and snapshot the current task buckets without
      // sending mail. Matches the pre-existing opt-in behaviour.
      derived.transitionNextSendDate = new Date().toISOString();
    }
  } else if (
    derived.sendEveryDays !== previous.sendEveryDays &&
    digestModeWantsSummary(derived.digestMode)
  ) {
    derived.nextSendDate = calculateNextSendDate(derived);
  }

  return derived;
}

// A single email-subscripton. Autosaving: the email address debounces and
// flushes on blur; the radios and dropdowns save immediately. The whole
// (derived) row is the payload, same shape the per-row Save button used to
// send.
const Subscription = ({sub, deletingId, onDelete, onSaved }) => {
  const {
    formState,
    setFormState,
    fieldErrors,
    flush,
  } = useAutosave({
    recordKey: `sub:${sub.id}`,
    // Seed with the effective digest mode so a legacy row with
    // notifyOnTaskStateChange=true renders (and saves) as "Both" rather
    // than losing the user's prior opt-in.
    original: { ...sub, digestMode: effectiveDigestMode(sub) },
    deriveState: deriveSubscriptionState,
    validate: (state) =>
      state && state.email && String(state.email).trim().length > 0
        ? { ok: true }
        : { ok: false, fieldErrors: { email: 'Email address is required.' } },
    buildPayload: (state) => ({ ...state }),
    save: (payload) => Database.updateEmailSubscription(payload),
    onSaved: (update) => {
      if (update) onSaved(sub.id, update);
    },
  });

  const digestMode = formState.digestMode;
  const wantsSummary = digestModeWantsSummary(digestMode);

  const handleToChange = (event) => {
    setFormState({ ...formState, email: event.target.value });
  }

  const handleFrequencyChange = (event) => {
    setFormState({ ...formState, sendEveryDays: event.target.value }, { immediate: true });
  }

  const handleSendHourGMTChange = (event) => {
    setFormState({ ...formState, sendHourGMT: event.target.value }, { immediate: true });
  }

  const handleDigestModeChange = (event) => {
    setFormState({ ...formState, digestMode: event.target.value }, { immediate: true });
  }

  const modeName = `subscription-${sub.id}-mode`;
  const freqId = `subscription-${sub.id}-frequency`;
  const emailId = `subscription-${sub.id}-email`;
  const hourId = `subscription-${sub.id}-hour`;

  const activeHelp = digestModeOptions.find((o) => o.value === digestMode)?.helpText;

  return (
      // Leaving any control flushes the autosave debounce (focusout bubbles).
      <div key={sub.id} className="w-100 text-start border-bottom pb-3 mb-3" onBlur={() => flush()}>
          <div className="row g-3 align-items-end">
              <div className="col-12">
                  <div className="form-label small mb-1">What to send</div>
                  <div
                    role="radiogroup"
                    aria-label="Digest mode"
                    className="d-flex flex-column flex-md-row flex-wrap gap-2 gap-md-3"
                  >
                      {digestModeOptions.map((option) => {
                          const optId = `${modeName}-${option.value}`;
                          return (
                              <div key={option.value} className="form-check">
                                  <input
                                    id={optId}
                                    className="form-check-input"
                                    type="radio"
                                    name={modeName}
                                    value={option.value}
                                    checked={digestMode === option.value}
                                    onChange={handleDigestModeChange}
                                  />
                                  <label className="form-check-label" htmlFor={optId}>
                                      {option.label}
                                  </label>
                              </div>
                          );
                      })}
                  </div>
                  {activeHelp && (
                      <div className="form-text mt-1">{activeHelp}</div>
                  )}
              </div>

              <div className="col-12 col-sm-6 col-lg-5">
                  <label className="form-label small mb-1" htmlFor={emailId}>
                      Email address
                  </label>
                  <input
                    id={emailId}
                    className={`form-control form-control-sm${fieldErrors.email ? ' is-invalid' : ''}`}
                    name='To'
                    type='text'
                    placeholder='email address'
                    value={formState.email ?? ''}
                    onChange={handleToChange}
                  />
                  {fieldErrors.email && (
                    <div className="text-danger small" role="alert">{fieldErrors.email}</div>
                  )}
              </div>

              {wantsSummary && (
                  <div className="col-12 col-sm-6 col-lg-2">
                      <label className="form-label small mb-1" htmlFor={freqId}>
                          Frequency
                      </label>
                      <select
                          id={freqId}
                          className="form-select form-select-sm"
                          name='Frequency'
                          value={formState.sendEveryDays}
                          onChange={handleFrequencyChange}
                      >
                          { frequencies.map((option) => (
                              <option key={option.name} value={option.numDays}>{option.name}</option>
                          ))}
                      </select>
                  </div>
              )}

              <div className="col-12 col-sm-6 col-lg-2">
                  <label className="form-label small mb-1" htmlFor={hourId}>
                      Send hour
                  </label>
                  <select
                      id={hourId}
                      className="form-select form-select-sm"
                      name='SendHour'
                      value={formState.sendHourGMT}
                      onChange={handleSendHourGMTChange}
                  >
                      { localOrderGmtHours().map ( (v) => (
                         <option key={v} value={v}>{localHourLabel(v)}</option>
                      ))}
                  </select>
              </div>

              <div className="col-12 col-sm-6 col-lg-3 d-flex flex-wrap align-items-center gap-3">
                  <DeleteButton
                    subId={sub.id}
                    deletingId={deletingId}
                    onDelete={onDelete}
                  />
              </div>
          </div>
      </div>
  );

}


export const EmailSubscriptionsCard = ( { user, item } ) => {
    const [deletingId, setDeletingId] = useState(null);

    // Loading/error UI
    const [StatusMessage, {showText, showError, hideStatus}] = useStatusMessage();

    async function handleAddSubscription() {
        try {
            // Show adding
            showText('Adding');

            // Add a subscription. Default to the periodic summary path --
            // it matches what the page used to create before digest modes
            // existed, so the e2e flow and existing user expectations are
            // unchanged. Users can switch to transitions/both per-row.
            let unsub = crypto.randomUUID();
            let subscription = {
                emailType: emailTypeItemStatus,
                email: user.email,
                name: item.name,
                sendHourGMT: 18,
                itemId: item.id,
                sendEveryDays: emailSendDaily,
                unsubscribeKey: unsub,
                notifyOnTaskStateChange: false,
                digestMode: digestModeSummary,
            }
            subscription.nextSendDate = calculateNextSendDate(subscription);

            let newSub = await Database.createEmailSubscription(subscription);

            // Add to model
            // TODO: Isn't item supposed to be read-only?
            item.emails.items.push(newSub);
    
            // Hide adding ui.
            hideStatus();
        } catch (err) {
            showError(err);
        }
    }


    async function onDelete(subId) {
        try {
            // Show deleting.
            setDeletingId(subId);

            // Cancel any pending or in-flight autosave for this row first --
            // a late update against the deleted row must not fire.
            saveQueue.cancel(`sub:${subId}`);

            // Delete on Server.
            await Database.deleteEmailSubscription(subId);

            // Update Model
            // TODO: Isn't item supposed to be read-only?
            item.emails.items = item.emails.items.filter( email => (email.id !== subId));

            setDeletingId(null);
        } catch(err) {
            // TODO: Next to delete button?
            showError(err);
        }
    }

    // Runs per successful autosave flush from a row: adopt the server's copy
    // into the in-memory list.
    function onSaved(subId, update) {
        // TODO: Isn't item supposed to be read-only?
        item.emails.items = item.emails.items.map( email => (email.id !== subId) ? email : update);
    }

    // No item, no UI...
    if (!item) {
        return null;
    }

    return (
        <div className="container pt-2">
            <div className="card">
                <div className="card-header text-center">
                  <h5>
                    <span className="text-center">
                      <strong>Manage Subscriptions</strong>
                    </span>
                  </h5>
                </div>

                <div className="container px-3 pb-3 text-start">
                    { item.emails.items.map( (sub) => (
                        <Subscription key={sub.id} sub={sub} deletingId={deletingId} onDelete={onDelete} onSaved={onSaved} />
                    ))}
                    <div className="text-center pt-2">
                      <Button onClick={handleAddSubscription} className="btn-sm">
                        Add
                      </Button>
                    </div>
                    <div className="text-center">
                      {StatusMessage()}
                    </div>
                </div>
            </div>
        </div>
    )
}

